/**
 * profile.ts — the profile view: wallet configuration and MFA enrolment.
 *
 * WHY A MODAL AND NOT A ROUTE. This app has no router, and adding one for two panels is more
 * machinery than the problem needs. A `position: fixed` overlay also cannot be clipped by the pane
 * grid's `overflow: hidden`, which an in-flow panel would be.
 *
 * WHAT MOVED HERE, and why the bottom panel lost its Wallet tab: a wallet is configuration, not a
 * live account view. Editing it sat one click from the position table — which is the wrong
 * neighbourhood for a field that accepts a signing key. Andrei asked for the indicator to move here
 * too, so the bottom panel no longer displays the address at all.
 *
 * PURE RENDERING + VALIDATION, NO FETCH. Same split as `wallet.ts` and `aichat.ts`: this module
 * returns HTML strings and validates input, so vitest can reach every branch without a DOM or a
 * backend. `main.ts` owns the network calls.
 *
 * THE MFA SECTION IS THE REASON THIS EXISTS NOW. Andrei asked to enforce MFA on the pool. Doing that
 * before an enrolment UI existed would have locked him out: `mfa: REQUIRED` makes Cognito issue an
 * MFA_SETUP challenge at next sign-in, and the login form had no handler for it — so the pool would
 * demand a factor the app could not answer, with no recovery except an administrator flipping MFA
 * back in the console. Capability first, enforcement second.
 */

/** What `/api/account/credentials` reports. The private key is NEVER part of this. */
export type WalletState = {
  configured: boolean;
  address: string | null;
  testnet?: boolean;
  key_present?: boolean;
  trading_enabled?: boolean;
  /**
   * Is this wallet subscribed to the daily stop-loss advisor?
   *
   * READ FROM THE WALLET PAYLOAD, WRITTEN TO `PUT /api/account/sl-monitor` — not a Telegram field.
   * The attribute lives on the users-table wallet row and its only writer is
   * `sl_monitor.set_enabled`, which refuses without an address (`attribute_exists(address)`). ABSENT
   * MEANS OFF, matching the storage default, so an API that predates the field renders no
   * subscription.
   */
  sl_monitor_enabled?: boolean;
};

/** Which panel the modal is showing. */
export type ProfileTab = 'wallet' | 'security' | 'alerts';

/**
 * What `/api/account/telegram` reports. The BOT TOKEN IS NEVER PART OF THIS.
 *
 * Not masked, not prefixed, not its length: a token is a credential — anyone holding it can post
 * as that bot — and a value that reaches a browser is a value in a devtools network log. The chat
 * id IS here, because it is an addressing detail rather than a secret and the user needs to
 * confirm which chat is wired up.
 */
export type AlertState = {
  chat_id: string | null;
  token_saved: boolean;
  alerts_enabled: boolean;
};

/**
 * Validate the alert settings before either value reaches the server.
 *
 * DUPLICATED ON THE SERVER ON PURPOSE, exactly as `validate` above is: the server MUST check —
 * it is the trust boundary and the value ends up inside a URL — and the browser checks so a typo
 * costs no round trip and no server log line about a rejected credential.
 *
 * PLAIN LANGUAGE, NO PATTERNS QUOTED. A message reading "must match ^[0-9]{5,20}:..." tells a
 * person nothing they can act on. These say what to go and get.
 */
export function validateAlerts(botToken: string, chatId: string): string | null {
  const t = botToken.trim();
  if (!t) return 'paste the token BotFather gave you';
  // SEED-PHRASE-STYLE ORDERING: name the recognisable mistake before the generic shape error. A
  // token with spaces in it is almost always a paste that picked up surrounding words.
  if (/\s/.test(t)) return 'a bot token has no spaces in it — check the paste';
  if (!/^[0-9]{5,20}:[A-Za-z0-9_-]{30,64}$/.test(t)) {
    return 'that does not look like a bot token — it is a number, then a colon, then a long ' +
      'string of letters and numbers';
  }

  const c = chatId.trim();
  if (!c) return 'add the chat id you want alerts in';
  if (c.startsWith('@')) {
    return 'that is a username, not a chat id — ask @userinfobot in Telegram for the number';
  }
  if (!/^-?[0-9]{1,19}$/.test(c)) return 'a chat id is a whole number, like 61193397 or -1001234567890';
  return null;
}

/**
 * The ALERTS panel: bring your own bot, get your own alerts.
 *
 * Andrei: "each user should have an ability to provide their telegram bot token and their user id
 * to get their unique scan results and alerts."
 *
 * WRITE-ONLY BY CONSTRUCTION. The server never returns the token, so this form cannot pre-fill it
 * and does not try — there is no "reveal", and re-saving is how you rotate. The only thing shown
 * about the token is whether one is saved.
 *
 * A MISSING VALUE IS AN HONEST ABSENCE IN WORDS, not an empty box or a dash: someone who has
 * saved a token but no chat id is told which half is missing, because the alerts will not arrive
 * and the reason is not guessable.
 */
export function renderAlertsPanel(state: AlertState | null, wallet?: WalletState | null): string {
  const s = state ?? { chat_id: null, token_saved: false, alerts_enabled: false };

  const status = s.alerts_enabled
    ? `<div id="tg-state">Alerts are on. Your morning scan goes to chat <b>${escapeHtml(
        s.chat_id ?? '',
      )}</b> through your own bot.</div>`
    : s.token_saved
      ? '<div id="tg-state">Your bot is saved, but there is no chat to send to yet. ' +
        'Add a chat id below and alerts will start tomorrow morning.</div>'
      : s.chat_id
        ? `<div id="tg-state">A chat is set (<b>${escapeHtml(s.chat_id)}</b>) but no bot is ` +
          'saved yet, so nothing can be sent. Add your bot token below.</div>'
        : '<div id="tg-state">Get the morning scan in your own Telegram. Create a bot with ' +
          '<b>@BotFather</b>, send it a message, then paste its token here.</div>';

  return `
    <h4>Daily scan alerts</h4>
    ${status}
    <form id="tg-form" autocomplete="off">
      <label for="tg-token">Bot token ${s.token_saved ? '(enter a new one to replace)' : ''}</label>
      <input id="tg-token" type="password" spellcheck="false" autocapitalize="off"
             autocomplete="new-password" placeholder="Paste the token from BotFather" />
      <label for="tg-chat">Chat id</label>
      <input id="tg-chat" type="text" spellcheck="false" autocapitalize="off"
             inputmode="numeric" placeholder="61193397"
             value="${escapeHtml(s.chat_id ?? '')}" />
      <div class="creds-actions">
        <button id="tg-save" type="submit">${s.token_saved ? 'Update' : 'Turn on'} alerts</button>
        ${s.token_saved || s.chat_id ? '<button id="tg-delete" type="button">Turn off</button>' : ''}
      </div>
    </form>
    <div id="tg-msg"></div>
    ${slMonitorBlock(wallet ?? null)}
    <div id="tg-warn">
      Your bot token lets anything holding it post as your bot, so it is stored encrypted and
      never shown again — not even to you. You can turn alerts off at any time, which deletes it.
      Not sure of your chat id? Message <b>@userinfobot</b> in Telegram and it will tell you.
    </div>`;
}

/**
 * The STOP-LOSS MONITORING opt-in. A SECOND, INDEPENDENT product on the alerts panel.
 *
 * NOT NESTED UNDER THE ALERTS TOGGLE, and not saved with it: the alerts form writes a bot token to
 * `/api/account/telegram`, this checkbox writes `{enabled}` to `PUT /api/account/sl-monitor`, and
 * the attribute it sets (`slMonitorEnabled`, on the wallet row) has exactly one writer. Two
 * products, two round-trips.
 *
 * WHAT EACH ONE IS, IN WORDS. The toggle above is the daily scan — new setups the system found.
 * This one watches POSITIONS you already hold and proposes moving a stop. Never places an order.
 *
 * THE WALLET GUARD IS THE DEFAULT PATH, NOT AN EDGE CASE: the server refuses to set this without an
 * address (`attribute_exists(address)`) and production holds zero wallet rows, so an ungated
 * checkbox would offer every real user today a control whose only outcome is an error. Disabled and
 * explained, pointing at the Wallet tab — never a green success for a flag that can never select.
 */
function slMonitorBlock(w: WalletState | null): string {
  const connected = w?.configured === true;
  const on = connected && w?.sl_monitor_enabled === true;
  return `
    <hr />
    <h4>Stop-loss monitoring</h4>
    <label class="creds-row">
      <input id="tg-slmon" type="checkbox" ${on ? 'checked' : ''} ${connected ? '' : 'disabled'} />
      Watch my open positions and message me when a stop can move
    </label>
    <div id="tg-slmon-msg">${
      connected
        ? ''
        : 'Stop-loss monitoring needs a connected wallet — there are no positions to watch ' +
          'without one. Connect it under <b>Wallet</b>, then come back and turn this on.'
    }</div>`;
}

/** `0x1234…abcd` — enough to recognise a wallet, short enough to read at a glance. */
export function shortAddress(addr: string | null): string {
  const a = (addr ?? '').trim();
  if (a.length <= 12) return a;
  return `${a.slice(0, 6)}…${a.slice(-4)}`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Validate a wallet address and signing key before either reaches the server.
 *
 * DUPLICATED ON THE SERVER ON PURPOSE (`exec_tools/secrets.py` re-checks with `re.fullmatch`), and a
 * parity test pins that the two AGREE on 12 cases. The browser can be bypassed, so the server must
 * check; the browser checks so a typo costs no round trip. That parity test already earned its place
 * — it caught the server ACCEPTING a 42-character all-non-hex address that the client rejected.
 */
export function validate(address: string, key: string): string | null {
  const a = address.trim();
  if (!a) return 'wallet address is required';
  if (!/^0x[0-9a-fA-F]{40}$/.test(a)) {
    return 'address must be 0x followed by 40 hex characters';
  }

  const k = key.trim();
  if (!k) return 'private key is required';

  // SEED-PHRASE CHECK BEFORE THE LENGTH RULE. A test caught this ordering: a 12-word phrase is
  // neither 64 hex chars nor a valid key, so the length rule fires first and reports "must be 64 hex
  // characters, got 92" — technically true and useless for telling someone they just pasted a
  // recovery phrase into a web form. That mistake costs a wallet, so it gets its own message.
  if (/\s/.test(k) && k.split(/\s+/).length >= 12) {
    return 'that looks like a seed phrase, not a private key — never paste a seed phrase anywhere';
  }

  const bare = k.startsWith('0x') ? k.slice(2) : k;
  if (!/^[0-9a-fA-F]{64}$/.test(bare)) {
    return `private key must be 64 hex characters (optionally 0x-prefixed), got ${bare.length}`;
  }
  return null;
}

/** The dropdown's identity line. Email when the token carries one, else the short subject. */
export function whoLabel(email: string | null, sub: string | null): string {
  if (email) return email;
  if (sub) return `${sub.slice(0, 8)}…`;
  return 'signed in';
}

/** The WALLET panel. */
export function renderWalletPanel(state: WalletState | null): string {
  const s = state ?? { configured: false, address: null };
  const armed = s.trading_enabled === true;

  // PLAIN LANGUAGE, NO IMPLEMENTATION DETAIL. Andrei: "we don't need to display technical
  // information on the page." No parameter stores, no environment variables, no subsystem names —
  // none of it helps someone decide what to type. A test asserts the forbidden vocabulary.
  const status = s.configured
    ? `<div id="creds-state">Connected to <b>${escapeHtml(shortAddress(s.address))}</b>` +
      `${s.testnet ? ' on <b>testnet</b>' : ''} — <b>${armed ? 'trading enabled' : 'view only'}</b>.</div>`
    : `<div id="creds-state">Connect a wallet to see your positions, orders and balances.</div>`;

  return `
    <h4>Wallet</h4>
    ${status}
    <form id="creds-form" autocomplete="off">
      <label for="creds-address">Account address — your main wallet, not the API wallet</label>
      <input id="creds-address" type="text" spellcheck="false" autocapitalize="off"
             placeholder="0x…" value="${escapeHtml(s.address ?? '')}" />
      <label for="creds-key">Private key ${s.key_present ? '(enter a new one to replace)' : ''}</label>
      <input id="creds-key" type="password" spellcheck="false" autocapitalize="off"
             autocomplete="new-password" placeholder="Paste your key" />
      <label class="creds-row">
        <input id="creds-testnet" type="checkbox" ${s.testnet ? 'checked' : ''} />
        Use testnet
      </label>
      <div class="creds-actions">
        <button id="creds-save" type="submit">${s.configured ? 'Update' : 'Connect'} wallet</button>
        ${s.configured ? '<button id="creds-delete" type="button">Disconnect</button>' : ''}
      </div>
    </form>
    <div id="creds-msg"></div>
    <div id="creds-warn">
      Your private key can <b>withdraw funds</b>, not just trade. Consider using a Hyperliquid
      <b>agent wallet</b> — it can place orders but cannot withdraw, so a mistake costs a bad trade
      rather than your balance. The key is encrypted, never shown again, and you can disconnect at
      any time.
    </div>`;
}

/** The SECURITY panel. `enrolling` carries a pending secret once enrolment has begun. */
export function renderSecurityPanel(opts: {
  enabled: boolean;
  enrolling?: { secret: string } | null;
  message?: string | null;
}): string {
  if (opts.enrolling) {
    return `
      <h4>Two-factor authentication</h4>
      <p class="profile-note">Scan this with your authenticator app, then enter the 6-digit code it
        shows. Keep the app — you will need a code every time you sign in.</p>
      <div id="mfa-qr"></div>
      <p class="profile-note">Cannot scan? Enter this key manually:</p>
      <div id="mfa-secret">${escapeHtml(opts.enrolling.secret)}</div>
      <form id="mfa-form" autocomplete="off">
        <label for="mfa-code">6-digit code</label>
        <input id="mfa-code" type="text" inputmode="numeric" autocomplete="one-time-code"
               maxlength="6" placeholder="000000" />
        <div class="creds-actions">
          <button id="mfa-verify" type="submit">Verify and enable</button>
          <button id="mfa-cancel" type="button">Cancel</button>
        </div>
      </form>
      <div id="mfa-msg">${escapeHtml(opts.message ?? '')}</div>`;
  }

  return `
    <h4>Two-factor authentication</h4>
    <div id="mfa-state">Status: <span class="${opts.enabled ? 'mfa-on' : 'mfa-off'}">${
      opts.enabled ? 'enabled' : 'not enabled'
    }</span></div>
    <p class="profile-note">${
      opts.enabled
        ? 'Your account asks for a code from your authenticator app at sign-in.'
        : 'Your session lasts a month, so a stolen browser profile is a month-long credential for ' +
          'your trading account. An authenticator app is the defence against that.'
    }</p>
    ${opts.enabled ? '' : '<button id="mfa-begin" type="button">Set up authenticator app</button>'}
    <div id="mfa-msg">${escapeHtml(opts.message ?? '')}</div>`;
}

/** Validate a TOTP code before spending a round trip on it. */
export function validateTotpCode(code: string): string | null {
  const c = code.trim();
  if (!/^[0-9]{6}$/.test(c)) return 'enter the 6 digits from your authenticator app';
  return null;
}
